/**
 * Application Constants
 * 
 * Centralized constants for UI elements, network settings, and application behavior.
 * This provides a single source of truth for commonly used strings and values,
 * enables auto-completion, and allows the compiler to catch typos.
 * 
 * Usage:
 * import { NETWORK_CONSTANTS, RECAPTCHA_CONSTANTS, CSS_SELECTORS } from '@/lib/constants/appConstants';
 */

/**
 * Brand and subdomain constants
 */
export const BRAND_CONSTANTS = Object.freeze({
  /** Supported brand identifiers */
  BRANDS: Object.freeze({
    TRACFONE: 'tracfone',
    TOTAL_WIRELESS: 'totalwireless',
    STRAIGHT_TALK: 'straighttalk',
    SIMPLE_MOBILE: 'simplemobile',
    NET10: 'net10',
    SAFELINK: 'safelink',
    FAMILY_MOBILE: 'familymobile',
    GO_SMART_MOBILE: 'gosmartmobile',
    VISIBLE: 'visible',
  } as const),
  /** Default brand for fallback */
  DEFAULT_BRAND: 'totalwireless' as const,
  /** Valid subdomain patterns */
  VALID_SUBDOMAINS: Object.freeze([
    'tracfone',
    'totalwireless',
    'straighttalk', 
    'simplemobile',
    'net10',
    'safelink',
    'familymobile',
    'gosmartmobile',
    'visible'
  ] as const),
} as const);

/**
 * Brand header sections data
 */
export const BRAND_SECTIONS = Object.freeze([
  {
    id: 'brand_straighttalk',
    image: { src: '/logos/straight-talk.svg', alt: 'Straight Talk' },
    cta: { type: 'link', text: 'Straight Talk', action: 'redirect', destination: 'https://www.straighttalk.com/' }
  },
  {
    id: 'brand_net10',
    image: { src: '/logos/net10.svg', alt: 'NET10' },
    cta: { type: 'link', text: 'NET10', action: 'redirect', destination: 'https://www.net10wireless.com/' }
  },
  {
    id: 'brand_clearway',
    image: { src: '/logos/clearway.svg', alt: 'Clearway' },
    cta: { type: 'link', text: 'Clearway', action: 'redirect', destination: 'https://www.tracfonewirelessinc.com/en/clearway' }
  },
  {
    id: 'brand_totalwireless',
    image: { src: '/logos/total.svg', alt: 'Total Wireless' },
    cta: { type: 'link', text: 'Total Wireless', action: 'redirect', destination: 'https://www.totalwireless.com/' }
  },
  {
    id: 'brand_tracfone',
    image: { src: '/logos/tracfone.svg', alt: 'Tracfone' },
    cta: { type: 'link', text: 'Tracfone', action: 'redirect', destination: 'https://www.tracfone.com/home' }
  },
  {
    id: 'brand_simplemobile',
    image: { src: '/logos/simplemobile.svg', alt: 'SimpleMobile' },
    cta: { type: 'link', text: 'SimpleMobile', action: 'redirect', destination: 'https://www.simplemobile.com/' }
  },
  {
    id: 'brand_safelink',
    image: { src: '/logos/safelink.svg', alt: 'SafeLink' },
    cta: { type: 'link', text: 'SafeLink', action: 'redirect', destination: 'https://www.safelinkwireless.com/en/#!/newHome' }
  },
  {
    id: 'brand_familymobile',
    image: { src: '/logos/familymobile.svg', alt: 'FamilyMobile' },
    cta: { type: 'link', text: 'FamilyMobile', action: 'redirect', destination: 'https://www.myfamilymobile.com/' }
  },
  {
    id: 'brand_gosmartmobile',
    image: { src: '/logos/gosmartmobile.svg', alt: 'GoSmartMobile' },
    cta: { type: 'link', text: 'GoSmartMobile', action: 'redirect', destination: 'https://www.gosmartmobile.com/' }
  },
  {
    id: 'language_spanish',
    image: { src: '/logos/spanish.svg', alt: 'Spanish' },
    cta: { type: 'link', text: 'Spanish', action: 'redirect', destination: '/es' }
  },
  {
    id: 'language_english',
    image: { src: '/logos/english.svg', alt: 'English' },
    cta: { type: 'link', text: 'English', action: 'redirect', destination: '/' }
  }
] as const);

/**
 * API endpoint constants
 */
export const API_CONSTANTS = Object.freeze({
  /** API endpoints */
  ENDPOINTS: Object.freeze({
    CHECK_ELIGIBILITY: '/api/check-eligibility',
    ORDER_SUMMARY: '/api/order-summary',
    HEALTH_CHECK: '/api/health',
  } as const),
  /** API base URLs for different environments */
  BASE_URLS: Object.freeze({
    // These would typically be environment-specific
    TOTALWIRELESS_SITA: 'https://sita.totalwireless.com/device/device-unlock',
  } as const),
} as const);

/**
 * Network connection constants
 */
export const NETWORK_CONSTANTS = Object.freeze({
  /** Connection types considered slow */
  SLOW_CONNECTION_TYPES: Object.freeze(['slow-2g', '2g', '3g'] as const),
  /** Connection types considered fast */
  FAST_CONNECTION_TYPES: Object.freeze(['4g', '5g'] as const),
  /** Network timeout values in milliseconds */
  TIMEOUTS: Object.freeze({
    DEFAULT: 30000,
    SLOW_NETWORK: 45000,
    FAST_NETWORK: 15000,
  } as const),
} as const);

/**
 * reCAPTCHA related constants
 */
export const RECAPTCHA_CONSTANTS = Object.freeze({
  /** reCAPTCHA actions */
  ACTIONS: Object.freeze({
    LOGIN: 'login',
    SUBMIT: 'submit',
    VERIFY: 'verify',
  } as const),
  /** reCAPTCHA timeouts in milliseconds */
  TIMEOUTS: Object.freeze({
    LOAD_WAIT: 10000,
    LOAD_WAIT_SLOW: 30000, // Extended timeout for slow networks
    EXECUTION: 30000,
    EXECUTION_SLOW: 60000, // Extended execution timeout for slow networks
    BADGE_RETRY: 5000,
  } as const),
  /** reCAPTCHA retry settings */
  RETRIES: Object.freeze({
    MAX_BADGE_RETRIES: 50,
    MAX_BADGE_RETRIES_SLOW: 100, // More retries for slow networks
    RETRY_INTERVAL: 100,
    RETRY_INTERVAL_SLOW: 250, // Longer intervals for slow networks
    MAX_RETRY_INTERVAL: 2000, // Cap for exponential backoff
  } as const),
  /** reCAPTCHA token validation settings */
  VALIDATION: Object.freeze({
    MIN_TOKEN_LENGTH: 600, // Minimum acceptable token length
    MAX_RETRY_DURATION: 5000, // Maximum time to spend retrying for short tokens (5 seconds)
    TOKEN_RETRY_DELAY: 1000, // Delay between token retries in milliseconds
    TOKEN_RETRY_DELAY_SLOW: 1500, // Extended delay for slow networks
  } as const),
  /** reCAPTCHA fallback constants */
  FALLBACK: Object.freeze({
    DEV_BYPASS_TOKEN: 'dev_bypass_token', // Token used when reCAPTCHA fails
    TIMEOUT_DURATION: 5000, // 5 seconds timeout for token generation
  } as const),
} as const);

/**
 * CSS selectors and DOM constants
 */
export const CSS_SELECTORS = Object.freeze({
  /** reCAPTCHA related selectors */
  RECAPTCHA: Object.freeze({
    BADGE: '.grecaptcha-badge',
    CONTAINER: '.grecaptcha-container',
  } as const),
  /** Common UI selectors */
  UI: Object.freeze({
    LOADING_SPINNER: '.loading-spinner',
    ERROR_MESSAGE: '.error-message',
    SUCCESS_MESSAGE: '.success-message',
  } as const),
} as const);

/**
 * API and form constants
 */
export const FORM_CONSTANTS = Object.freeze({
  /** Form field names */
  FIELDS: Object.freeze({
    IMEI: 'imei_field',
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    EMAIL: 'email',
    PHONE: 'contactPhone',
  } as const),
  /** Validation constants */
  VALIDATION: Object.freeze({
    IMEI_LENGTH: 15,
    PHONE_MIN_LENGTH: 10,
    EMAIL_MAX_LENGTH: 254,
  } as const),
} as const);

/**
 * Route and navigation constants
 */
export const ROUTE_CONSTANTS = Object.freeze({
  /** Status routes */
  STATUS: Object.freeze({
    LOCKED: 'locked',
    UNLOCKED: 'unlocked',
    ELIGIBLE: 'eligible',
    INELIGIBLE: 'ineligible',
    PENDING: 'pending',
    PHONE_NOT_ACTIVATED: 'phone-not-activated',
    DELAYED: 'delayed',
  } as const),
  /** Base paths */
  PATHS: Object.freeze({
    DEVICE_UNLOCK: '/device',
    STATUS: '/status',
    API: '/api',
  } as const),
} as const);

/**
 * Error and message constants
 */
export const MESSAGE_CONSTANTS = Object.freeze({
  /** Error messages */
  ERRORS: Object.freeze({
    NETWORK_TIMEOUT: 'Network request timed out. Please check your connection and try again.',
    RECAPTCHA_FAILED: 'reCAPTCHA verification failed. Please try again.',
    RECAPTCHA_TIMEOUT: 'reCAPTCHA verification timed out. Please try again.',
    RECAPTCHA_SLOW_NETWORK: 'reCAPTCHA verification failed due to slow network. Please check your connection and try again.',
    RECAPTCHA_TOKEN_SHORT: 'reCAPTCHA verification incomplete. The system will retry automatically.',
    INVALID_IMEI: 'Invalid IMEI format. Please enter a valid 15-digit IMEI.',
    API_ERROR: 'Oops, something went wrong! Please check your connection and try again, or come back later if the issue persists.',
    SERVER_UNAVAILABLE: 'Server is temporarily unavailable. Please try again in a few minutes.',
    INVALID_REQUEST: 'Invalid request. Please check your IMEI and try again.',
    CASE_SUBMISSION_FAILED: 'Case submission failed',
    FAILED_TO_CREATE_CASE: 'Failed to create case',
    NO_CASE_RESPONSE_DATA: 'No case response data received',
    FORM_SUBMISSION_FAILED: 'Failed to submit form. Please try again.',
    
    // Validation errors
    IMEI_REQUIRED: 'IMEI is required',
    IMEI_INVALID_FORMAT: 'IMEI must be exactly 15 digits',
    EMAIL_REQUIRED: 'Email is required',
    EMAIL_INVALID_FORMAT: 'Invalid email format',
    EMAIL_TOO_LONG: 'Email is too long',
    PHONE_REQUIRED: 'Phone number is required',
    PHONE_INVALID_FORMAT: 'Invalid phone number format',
    FIELD_REQUIRED: 'This field is required',
    FIELD_EMPTY: 'This field cannot be empty',
    INPUT_TOO_LONG: 'Input is too long (max 100 characters)',
    INVALID_CHARACTERS: 'Invalid characters detected',
    
    // reCAPTCHA specific errors
    RECAPTCHA_CONFIG_ERROR: 'reCAPTCHA configuration error',
    RECAPTCHA_TOKEN_REQUIRED: 'reCAPTCHA token is required',
    RECAPTCHA_TOKEN_INVALID: 'Invalid reCAPTCHA token',
    RECAPTCHA_REQUEST_MALFORMED: 'reCAPTCHA request malformed',
    RECAPTCHA_TOKEN_EXPIRED: 'reCAPTCHA token has expired or was already used',
    RECAPTCHA_VERIFICATION_FAILED: 'reCAPTCHA verification failed',
  } as const),
  /** Success messages */
  SUCCESS: Object.freeze({
    FORM_SUBMITTED: 'Form submitted successfully',
    DATA_LOADED: 'Data loaded successfully',
  } as const),
  /** Loading messages */
  LOADING: Object.freeze({
    CHECKING_DEVICE: 'Checking device status...',
    LOADING_DATA: 'Loading...',
    VERIFYING_RECAPTCHA: 'Verifying...',
    PROCESSING: 'Processing...',
    LOADING_RECAPTCHA: 'Loading...',
    SLOW_NETWORK_WARNING: 'Slow connection detected. This may take longer than usual...',
    RECAPTCHA_LOADING_SLOW: 'Loading security verification (this may take a moment on slow connections)...',
    RECAPTCHA_NOT_READY: 'Security verification is still loading. Please wait...',
  } as const),
  /** Spanish localized messages */
  SPANISH: Object.freeze({
    ERRORS: Object.freeze({
      NETWORK_TIMEOUT: 'La solicitud de red agotó el tiempo. Verifica tu conexión e inténtalo de nuevo.',
      RECAPTCHA_FAILED: 'La verificación de reCAPTCHA falló. Inténtalo de nuevo.',
      RECAPTCHA_TIMEOUT: 'La verificación de reCAPTCHA agotó el tiempo. Inténtalo de nuevo.',
      RECAPTCHA_SLOW_NETWORK: 'La verificación de reCAPTCHA falló debido a una red lenta. Verifica tu conexión e inténtalo de nuevo.',
      RECAPTCHA_TOKEN_SHORT: 'La verificación de reCAPTCHA está incompleta. El sistema reintentará automáticamente.',
      INVALID_IMEI: 'Formato de IMEI inválido. Ingresa un IMEI válido de 15 dígitos.',
      API_ERROR: '¡Ups! Algo salió mal. Verifica tu conexión e inténtalo de nuevo, o regresa más tarde si el problema persiste.',
      SERVER_UNAVAILABLE: 'El servicio no está disponible temporalmente. Inténtalo más tarde.',
      INVALID_REQUEST: 'Solicitud inválida. Verifica tu información e inténtalo de nuevo.',
      CASE_SUBMISSION_FAILED: 'Error al enviar el caso',
      FAILED_TO_CREATE_CASE: 'No se pudo crear el caso',
      NO_CASE_RESPONSE_DATA: 'No se recibieron datos de respuesta del caso',
      FORM_SUBMISSION_FAILED: 'No se pudo enviar el formulario. Inténtalo de nuevo.',

      IMEI_REQUIRED: 'El IMEI es obligatorio',
      IMEI_INVALID_FORMAT: 'El IMEI debe tener exactamente 15 dígitos',
      EMAIL_REQUIRED: 'El correo electrónico es obligatorio',
      EMAIL_INVALID_FORMAT: 'Formato de correo electrónico inválido',
      EMAIL_TOO_LONG: 'El correo electrónico es demasiado largo',
      PHONE_REQUIRED: 'El número de teléfono es obligatorio',
      PHONE_INVALID_FORMAT: 'Formato de número de teléfono inválido',
      FIELD_REQUIRED: 'Este campo es obligatorio',
      FIELD_EMPTY: 'Este campo no puede estar vacío',
      INPUT_TOO_LONG: 'La entrada es demasiado larga (máx. 100 caracteres)',
      INVALID_CHARACTERS: 'Se detectaron caracteres inválidos',

      RECAPTCHA_CONFIG_ERROR: 'Error de configuración de reCAPTCHA',
      RECAPTCHA_TOKEN_REQUIRED: 'El token de reCAPTCHA es obligatorio',
      RECAPTCHA_TOKEN_INVALID: 'Token de reCAPTCHA inválido',
      RECAPTCHA_REQUEST_MALFORMED: 'Solicitud de reCAPTCHA mal formada',
      RECAPTCHA_TOKEN_EXPIRED: 'El token de reCAPTCHA ha expirado o ya fue usado',
      RECAPTCHA_VERIFICATION_FAILED: 'La verificación de reCAPTCHA falló',
    } as const),
    SUCCESS: Object.freeze({
      FORM_SUBMITTED: 'Formulario enviado con éxito',
      DATA_LOADED: 'Datos cargados con éxito',
    } as const),
    LOADING: Object.freeze({
      CHECKING_DEVICE: 'Verificando el estado del dispositivo...',
      LOADING_DATA: 'Cargando...',
      VERIFYING_RECAPTCHA: 'Verificando...',
      PROCESSING: 'Procesando...',
      LOADING_RECAPTCHA: 'Cargando...',
      SLOW_NETWORK_WARNING: 'Se detectó una conexión lenta. Esto puede tardar más de lo normal...',
      RECAPTCHA_LOADING_SLOW: 'Cargando verificación de seguridad (esto puede tardar un momento en redes lentas)...',
      RECAPTCHA_NOT_READY: 'La verificación de seguridad aún se está cargando. Espera por favor...',
    } as const),
  } as const),
} as const);

/**
 * Default UI text constants
 */
export const UI_TEXT_CONSTANTS = Object.freeze({
  /** Default titles and labels */
  DEFAULTS: Object.freeze({
    PAGE_TITLE: 'brand_name: Device Unlock',
    PAGE_DESCRIPTION: 'Generated by brand_name: Device Unlock',
    CHECK_UNLOCK_STATUS: 'Check Unlock Status',
    DEVICE_UNLOCKING_POLICY: 'Device Unlocking Policy',
    UNLOCK_POLICY: 'unlock policy',
    VIEW_OUR: 'View our ',
    IMEI_INPUT_LABEL: 'IMEI',
    IMEI_FIELD_REQUIRED: 'IMEI is required',
    IMEI_FIELD_INVALID: 'IMEI must be 15 digits',
    IMEI_TOOL_TIP_TITLE: '<p><b>Tips on how to find your IMEI, Serial Number, or Digital SIM IMEI:</b></p>',
    DESCRIPTION_IMEI_TOOLTIP1: '<p>If you have an iPhone or iPad, go to <b>Settings > General > About</b>. Look for one of the following options:</br><ul class="list-disc ml-6 space-y-2"><li>AVAILABLE SIM</li><li>ESIM</li><li>The name / label you assigned to your eSIM, along with IMEI or IMEI2 beneath it.</li></ul></p>',
    DESCRIPTION_IMEI_TOOLTIP2: '<p>Please note the Digital SIM (eSIM) capability may vary based on the iPhone model.</br><ul class="list-disc ml-6 space-y-2"><li>For iPhones 12, the IMEI2 is eSIM capable.</li><li>For iPhones SE 3rd Gen, 13, & 14, & 15, the IMEI and IMEI2 are both eSIM capable. (The Straight Talk’s iPhone models 13 & SE 3rd Gen, only the IMEI2 is eSIM capable)</li></ul></p>',
    DESCRIPTION_IMEI_TOOLTIP3: '<p>*Currently, we only support eSIM activation for these iPhone models: 12, 13, SE 3rd generation, and 14 and 15.</br><ul class="list-disc ml-6 space-y-2"><li>If you have an Android device, go to <b>Settings > About Device (or About Phone) > Status</b>. Some devices have an extra step: IMEI information.</li></ul></p>',
    DESCRIPTION_IMEI_TOOLTIP4: '<p>Note: You can also find your Digital SIM IMEI by entering <b>*#06#</b> on your keypad.</br><ul class="list-disc ml-6 space-y-2"><li>If you have a Straight Talk feature phone, go to Menu > Prepaid > Serial Number.</li></ul></p>',
    CHECK_STATUS: 'Check Status',
    CLOSE_CTA: 'Close',
    TITLE_DEVICE_STATUS: 'Device Status',
    STATUS_LABEL: 'Status:',
    DEVICE_LABEL: 'Device ID / IMEI:',
    REASON_LABEL: 'Reason',
    FIRST_NAME_REQUIRED: 'First name is required',
    LAST_NAME_REQUIRED: 'Last name is required',
    EMAIL_REQUIRED: 'Email is required',
    EMAIL_INVALID: 'Enter a valid email',
    PHONE_REQUIRED: 'Contact phone number is required',
    PHONE_INVALID: 'Enter a valid phone number',
    SUBMIT_CTA: 'Submit',
    BACK_CTA: 'Back',
    FIRST_NAME: 'First name*',
    LAST_NAME: 'Last name*',
    EMAIL: 'Email',
    CONTACT_PHONE: 'Contact phone number',
    FIRST_NAME_PLACEHOLDER: 'First name',
    LAST_NAME_PLACEHOLDER: 'Last name',
    EMAIL_PLACEHOLDER: 'Name@email.com',
    CONTACT_PHONE_PLACEHOLDER: '123 456 7890',
    TEXT_INTRO: 'Please review the eligibility criteria below:',
    TEXT_LIST_CRITERIA: 'Eligibility Criteria',
    UNLOCK_PHONE_TEXT: 'If your device is eligible for unlocking, please provide the following information to proceed:',
    TEXT_TITLE_BRANDS: 'Supported Brands',
    TEXT_LIST_BRANDS: 'Tracfone, Total Wireless, Straight Talk, Simple Mobile, Net10, Safelink, Family Mobile, Visible',
    TEXT_TITLE_SAFELINK: 'Safelink Specific Information',
    TEXT_LIST_SAFELINK: 'Safelink devices may have additional requirements. Please check with Safelink for more details.',
    TEXT_NOTE_LABEL: 'Note:',
    TEXT_NOTE_BODY: 'Unlocking your device may void your warranty. Please ensure you understand the implications before proceeding.',
    UNLOCK_INSTRUCTIONS_TITLE_1: 'How to Unlock Your Device',
    UNLOCK_INSTRUCTIONS_TEXT_1: 'Once you have confirmed your device is eligible, follow the instructions provided to complete the unlocking process.',
    UNLOCK_INSTRUCTIONS_TITLE_2: 'Need Help?',
    UNLOCK_INSTRUCTIONS_TEXT_2: 'If you encounter any issues or have questions about unlocking your device, please contact our support team for assistance.',
    TITLE_ORDER_SUMMARY: 'Order Summary',
    DESCRIPTION_ORDER_SUMMARY: 'Review your order details below before submitting your unlock request.',

  } as const),
} as const);

/**
 * Styling constants for consistent UI appearance
 */
export const STYLE_CONSTANTS = Object.freeze({
  /** Font families used throughout the application */
  FONTS: Object.freeze({
    /** Primary brand font stack */
    PRIMARY: 'Verizon NHG DS, Helvetica Neue, Helvetica, Arial, sans-serif',
    /** Fallback sans-serif font stack */
    SANS_SERIF: 'Helvetica Neue, Helvetica, Arial, sans-serif',
  } as const),
} as const);

/**
 * reCAPTCHA error code mappings
 */
export const RECAPTCHA_ERROR_MAPPINGS = Object.freeze({
  'missing-input-secret': MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_CONFIG_ERROR,
  'invalid-input-secret': MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_CONFIG_ERROR,
  'missing-input-response': MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_TOKEN_REQUIRED,
  'invalid-input-response': MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_TOKEN_INVALID,
  'bad-request': MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_REQUEST_MALFORMED,
  'timeout-or-duplicate': MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_TOKEN_EXPIRED,
} as const);

// Export all constants for convenience
export const APP_CONSTANTS = Object.freeze({
  BRANDS: BRAND_CONSTANTS,
  NETWORK: NETWORK_CONSTANTS,
  RECAPTCHA: RECAPTCHA_CONSTANTS,
  CSS: CSS_SELECTORS,
  FORMS: FORM_CONSTANTS,
  ROUTES: ROUTE_CONSTANTS,
  MESSAGES: MESSAGE_CONSTANTS,
  UI_TEXT: UI_TEXT_CONSTANTS,
} as const);

// Type exports for better TypeScript support
export type NetworkConnectionType = typeof NETWORK_CONSTANTS.SLOW_CONNECTION_TYPES[number] | typeof NETWORK_CONSTANTS.FAST_CONNECTION_TYPES[number];
export type RecaptchaAction = typeof RECAPTCHA_CONSTANTS.ACTIONS[keyof typeof RECAPTCHA_CONSTANTS.ACTIONS];
export type StatusRoute = typeof ROUTE_CONSTANTS.STATUS[keyof typeof ROUTE_CONSTANTS.STATUS];
export type SupportedBrand = typeof BRAND_CONSTANTS.BRANDS[keyof typeof BRAND_CONSTANTS.BRANDS];
